// S1 · Såter på marken: rena funktioner för att läsa, rita in, döpa om och ta bort såter.
// Såterna är delområdena (sub_area) i markens coordinates_json, samma som K6 ritar och
// importen/demomarken skapar. API:t läser dem med samma regler (apps/api/src/services/satar.ts).
import type { SatOccupancy } from '../api';

export interface SatShape { id: string; name: string; geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: any } }

type FC = { type: 'FeatureCollection'; features: any[] };

/** Samma som satFallbackId i API:t: äldre delområden utan id får ett id från formen. */
export function satFallbackId(geometry: unknown): string {
  const s = JSON.stringify((geometry as any)?.coordinates ?? null);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return `sat-${(h >>> 0).toString(16).padStart(8, '0')}`;
}

export const newSatId = () => `sat-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function toFc(json: unknown): FC {
  let v: any = json;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch { v = null; } }
  if (v?.type === 'FeatureCollection' && Array.isArray(v.features)) return { type: 'FeatureCollection', features: v.features };
  if (v?.type === 'Feature') return { type: 'FeatureCollection', features: [v] };
  if (v?.type === 'Polygon' || v?.type === 'MultiPolygon') return { type: 'FeatureCollection', features: [{ type: 'Feature', properties: { type: 'boundary' }, geometry: v }] };
  return { type: 'FeatureCollection', features: [] };
}

const isSat = (f: any) => f?.properties?.type === 'sub_area' && (f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon');

/** Id:t som API:t ser för varje delområde (properties.id, annars formens id; dubbletter får index). */
function idsOf(features: any[]): (string | null)[] {
  const seen = new Set<string>();
  return features.map((f, i) => {
    if (f?.properties?.type !== 'sub_area' || !f.geometry) return null;
    let id = typeof f.properties.id === 'string' && f.properties.id ? f.properties.id : satFallbackId(f.geometry);
    if (seen.has(id)) id = `${id}-${i}`;
    seen.add(id);
    return id;
  });
}

export function parseSatar(json: unknown): SatShape[] {
  const fc = toFc(json);
  const ids = idsOf(fc.features);
  const out: SatShape[] = [];
  fc.features.forEach((f, i) => {
    if (!isSat(f) || !ids[i]) return;
    const name = typeof f.properties.name === 'string' && f.properties.name.trim() ? f.properties.name.trim() : `Såt ${out.length + 1}`;
    out.push({ id: ids[i]!, name, geometry: f.geometry });
  });
  return out;
}

/** Skriver in id:n på alla delområden så att de inte ändras när formen ändras. */
function withIds(fc: FC): FC {
  const ids = idsOf(fc.features);
  return { ...fc, features: fc.features.map((f, i) => ids[i] && f.properties?.id !== ids[i] ? { ...f, properties: { ...f.properties, id: ids[i] } } : f) };
}

/** Lägger till en såt eller ersätter formen/namnet på en befintlig. Resten av gränsdatan behålls. */
export function upsertSat(json: unknown, sat: { id?: string | null; name: string; geometry?: SatShape['geometry'] | null }): string {
  const fc = withIds(toFc(json));
  const name = sat.name.trim().slice(0, 60) || 'Såt';
  const i = sat.id ? fc.features.findIndex(f => isSat(f) && f.properties.id === sat.id) : -1;
  if (i >= 0) {
    const f = fc.features[i];
    fc.features[i] = { ...f, properties: { ...f.properties, name }, geometry: sat.geometry ?? f.geometry };
  } else {
    if (!sat.geometry) throw new Error('Rita såten på kartan först');
    fc.features = [...fc.features, { type: 'Feature', properties: { type: 'sub_area', id: sat.id || newSatId(), name }, geometry: sat.geometry }];
  }
  return JSON.stringify(fc);
}

export function removeSat(json: unknown, id: string): string {
  const fc = withIds(toFc(json));
  return JSON.stringify({ ...fc, features: fc.features.filter(f => !(isSat(f) && f.properties.id === id)) });
}

/** Är punkten inne i polygonen (ray casting; hål räknas)? Koordinater i GeoJSON är [lng, lat]. */
export function pointInSat(lat: number, lng: number, geometry: SatShape['geometry']): boolean {
  const inRing = (ring: [number, number][]) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i], [xj, yj] = ring[j];
      if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  const polys: [number, number][][][] = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  return polys.some(rings => rings.length > 0 && inRing(rings[0]) && !rings.slice(1).some(inRing));
}

/**
 * Ligger såten inom markgränsen? Minst hälften av hörnen måste ligga inne i gränsen (gränsen är ofta
 * ungefärlig, så en såt som går lite utanför godtas). Utan markgräns godtas allt.
 */
export function satInsideBoundary(json: unknown, geometry: SatShape['geometry']): boolean {
  const borders = toFc(json).features.filter(f => f?.properties?.type !== 'sub_area' && (f?.geometry?.type === 'Polygon' || f?.geometry?.type === 'MultiPolygon'));
  if (!borders.length) return true;
  const rings: [number, number][][] = geometry.type === 'Polygon' ? [geometry.coordinates[0]] : geometry.coordinates.map((p: any) => p[0]);
  const pts = rings.flat();
  if (!pts.length) return false;
  const inside = pts.filter(([lng, lat]) => borders.some(b => pointInSat(lat, lng, b.geometry))).length;
  return inside * 2 >= pts.length;
}

// ─── Beläggning ───────────────────────────────────────────────────

export interface SatBusy { satId: string; items: SatOccupancy[] }

/** Vad som upptar varje såt (bokningar för hela marken räknas på alla såter). `exceptKey` = den egna jakten. */
export function busyBySat(satar: SatShape[], occ: SatOccupancy[], exceptKey?: string | null): Map<string, SatOccupancy[]> {
  const m = new Map<string, SatOccupancy[]>();
  for (const s of satar) m.set(s.id, []);
  for (const o of occ) {
    if (exceptKey && o.key === exceptKey) continue;
    for (const s of satar) if (o.wholeMark || o.satIds.includes(s.id)) m.get(s.id)!.push(o);
  }
  return m;
}

/** Kort text om en bokning: "Älgjakt 07:30–15:00" eller "Enskild jakt (bokad)". */
export function occupancyLabel(o: SatOccupancy): string {
  const hm = (iso: string | null) => iso ? new Date(iso).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Stockholm' }) : '';
  if (o.kind === 'ad') return `${o.title} (bokad)`;
  const time = o.startTime ? ` ${hm(o.startTime)}${o.endTime ? `–${hm(o.endTime)}` : ''}` : '';
  return `${o.status === 'active' ? 'Pågår: ' : ''}${o.title}${time}${o.wholeMark ? ' · hela marken' : ''}`;
}

/** Dagens datum i Sverige (YYYY-MM-DD). */
export function stockholmToday(now = new Date()): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** Färger per såt (skiljer såterna åt på kartan). */
export const SAT_COLORS = ['#00A2FF', '#8e44ad', '#16a085', '#d35400', '#2c3e50', '#c0392b', '#7f8c8d'];
export const satColor = (i: number) => SAT_COLORS[i % SAT_COLORS.length];
export const SAT_BUSY_COLOR = '#e03131';
